import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import {
  AgentEvents,
  RevealedCredential,
  RevealTaskCredentialsResponse,
} from '@dreamai/types';
import { usePusher } from '../../../../contexts/PusherContext';
import api from '../../../../api';
import CloseTaskModal from './CloseTaskModal';
import HumanTaskCredentialCreationForm from './HumanTaskCredentialCreationForm';
import './HumanTaskDisplay.scss';

interface PhoneCallTaskDetails {
  phoneNumber: string;
  contactName?: string;
  purpose: string;
  scriptGuidance?: string;
  expectedDuration?: number;
}

interface WebActionTaskDetails {
  url: string;
  /** Required for new tasks, but may be missing in legacy data */
  actionType?: 'form_fill' | 'booking' | 'information_retrieval' | 'other';
  instructions: string;
  formFields?: Record<string, string>;
  expectedOutcome?: string;
}

interface BrowseSessionTaskDetails {
  platform: string;
  url: string;
  browsingBrief?: string;
  numberOfOptions?: number;
  responseFormat?: string;
}

type HumanTaskDetails =
  | PhoneCallTaskDetails
  | WebActionTaskDetails
  | BrowseSessionTaskDetails;

interface HumanTaskDisplayProps {
  taskId: string;
  taskType: 'phone_call' | 'web_action' | 'in_person' | 'browse_session';
  taskTypeLabel: string;
  title: string;
  description: string;
  taskDetails: HumanTaskDetails;
  status: string;
  deadline?: string;
  connecteamStatus?: string | null;
  connecteamWorkerName?: string | null;
  readOnly?: boolean;
  acceptedAt?: string | null;
  connecteamTaskId?: string | null;
  hasCredentials?: boolean;
  hasMandatePayment?: boolean;
  /**
   * Whether the attached spending mandate is on the Assistant Card / VCC
   * (`paymentMethod = 'agent_card'`). Embedded into the Payment 3DS
   * templated message so the ops-facing assistant knows the mandate
   * type without having to call a tool to look it up — the assistant
   * branches its handling on `agent_card` vs `user_card`.
   */
  hasVcc?: boolean;
  /**
   * The mandate's payment method (`agent_card`, `user_card`, or
   * `merchant_stored`). Used to build the hidden ops-context line
   * in Payment 3DS messages.
   */
  mandatePaymentMethod?: string | null;
  /**
   * The active ops chat thread for this human task. Used by the User
   * MFA / Payment 3DS quick actions to post a templated message into
   * the chat so the assistant fires `requestUserCode` without the ops
   * worker having to type anything from scratch.
   */
  threadId?: string;
  onTaskClosed?: () => void;
}

/**
 * User MFA subtypes — any site-side verification challenge the user
 * themselves can satisfy by reading a code from their phone / email /
 * authenticator app, or approving a push. This is intentionally NOT
 * restricted to logged-in flows — sites often demand a code during
 * guest checkout, phone-number verification, account recovery, table
 * bookings, etc. Use Payment 3DS instead when the challenge is
 * specifically a card-verification step at checkout.
 */
type UserMfaSubtype =
  | 'sms_code'
  | 'email_code'
  | 'authenticator_code'
  | 'push_approval'
  | 'other';

/**
 * Payment 3DS subtypes — forms of card-verification challenge during
 * checkout. The subtype affects the operator-facing template text,
 * the hidden assistant context line, and (at the backend) whether
 * the modal renders code-input (`card_3ds`) or approval-only
 * (`push_approval` for `app_push`).
 *
 * `app_redirect` is intentionally NOT supported — a redirect-to-bank
 * 3DS flow lands in the operator's browser session, which neither the
 * cardholder nor the Airwallex admin has access to. Operators should
 * pick "Other" in that case.
 */
type Payment3dsSubtype = 'sms' | 'email' | 'app_push' | 'cvv_entry' | 'other';

/**
 * The two top-level MFA categories the operator can request. User MFA
 * goes to the user (their phone / email / app) — applies to any
 * site-side verification challenge, login or otherwise. Payment 3DS
 * is specifically a card-verification challenge whose code goes to
 * the cardholder (the user on a `user_card` mandate, the Airwallex
 * admin on a VCC mandate — the assistant disambiguates via the
 * hidden ops-context line attached at send time).
 */
type MfaCategory =
  | { kind: 'user_mfa'; subtype: UserMfaSubtype }
  | { kind: 'payment_3ds'; subtype: Payment3dsSubtype };

/**
 * Templated chat message per User MFA subtype — pre-fills the inline
 * editor. The framing is intentionally neutral on whether a login is
 * in progress: sites can ask for verification codes during guest
 * checkout, phone-number confirmation, account recovery, etc. The
 * operator can tweak the message before sending.
 */
const USER_MFA_TEMPLATES: Record<
  UserMfaSubtype,
  { label: string; message: string }
> = {
  sms_code: {
    label: 'SMS code',
    message:
      "The site is asking for an SMS verification code sent to the user's phone. Can you ask them for it?",
  },
  email_code: {
    label: 'Email code',
    message:
      "The site sent an email verification code. Can you check the user's connected emails or ask them for it?",
  },
  authenticator_code: {
    label: 'Authenticator code',
    message:
      "The site is asking for a code from the user's authenticator app (e.g. Google Authenticator, 1Password, Authy). Can you ask them for the current 6-digit code?",
  },
  push_approval: {
    label: 'Push approval',
    message:
      'The site is asking the user to approve this on their device (push notification). Can you ask them to approve it and confirm when done?',
  },
  other: {
    label: 'Other',
    message: "I need the user's help with a site verification challenge.",
  },
};

/**
 * Templated chat message per Payment 3DS subtype — pre-fills the inline
 * editor. Kept short so the operator can read and tweak quickly. The
 * mandate-type disambiguation (VCC vs. user_card) is appended
 * INVISIBLY at send time via `buildAssistantContext` — the operator
 * never sees that part, the assistant always does.
 */
const PAYMENT_3DS_TEMPLATES: Record<
  Payment3dsSubtype,
  { label: string; message: string }
> = {
  sms: {
    label: 'SMS code',
    message:
      'The checkout is asking for an SMS code from the bank to verify this payment. Can you get it?',
  },
  email: {
    label: 'Email code',
    message:
      'The checkout is asking for an email code from the bank to verify this payment. Can you get it?',
  },
  app_push: {
    label: 'App approval',
    message:
      'The checkout is asking the cardholder to approve this payment in their banking app (push notification). Can you confirm when done?',
  },
  cvv_entry: {
    label: 'CVV needed',
    message:
      'The checkout is asking for the CVV/security code of the card saved at the merchant. Can you ask the user for it?',
  },
  other: {
    label: 'Other',
    message:
      'The checkout is showing a card-verification challenge. Can you help?',
  },
};

/**
 * Build the hidden assistant-only context line that's appended to the
 * operator's message at send time. The operator never sees this in the
 * editor; the assistant uses it to branch behavior (e.g. refuse VCC
 * 3DS, search emails for user_card 3DS, etc.). The format is documented
 * in the MFA capability YAML so the assistant knows where to look.
 */
const buildAssistantContext = (
  draft: MfaCategory,
  hasVcc: boolean,
  mandatePaymentMethod?: string | null,
): string | null => {
  if (draft.kind !== 'payment_3ds') return null;
  const mandate = hasVcc
    ? 'agent_card (VCC)'
    : mandatePaymentMethod === 'merchant_stored'
      ? 'merchant_stored'
      : 'user_card';
  return `(ops-context: payment_3ds | mandate=${mandate} | subtype=${draft.subtype})`;
};

const connecteamStatusLabel = (status: string): string => {
  switch (status) {
    case 'published':
      return 'Sent to ConnectTeam';
    case 'assigned':
      return 'Assigned';
    case 'completed':
      return 'Completed';
    default:
      return status;
  }
};

const connecteamStatusColor = (status: string): string => {
  switch (status) {
    case 'published':
      return 'ct-status-published';
    case 'assigned':
      return 'ct-status-assigned';
    case 'completed':
      return 'ct-status-completed';
    default:
      return '';
  }
};

const HumanTaskDisplay: React.FC<HumanTaskDisplayProps> = ({
  taskId,
  taskType,
  taskTypeLabel,
  title,
  description,
  taskDetails,
  status,
  deadline,
  connecteamStatus,
  connecteamWorkerName,
  readOnly = false,
  acceptedAt,
  connecteamTaskId: initialConnecteamTaskId,
  hasCredentials: initialHasCredentials,
  hasMandatePayment,
  hasVcc,
  mandatePaymentMethod,
  threadId,
  onTaskClosed,
}) => {
  const { getAccessTokenSilently } = useAuth0();
  const navigate = useNavigate();
  const [showCloseModal, setShowCloseModal] = useState(false);
  const pusherContext = usePusher(true) as any;
  const opsChannel = pusherContext?.opsChannel;
  const [copied, setCopied] = useState(false);

  // Send to Field state
  const [isPushingToField, setIsPushingToField] = useState(false);
  const [pushSuccess, setPushSuccess] = useState(!!initialConnecteamTaskId);
  const [hasCredentialsState, setHasCredentialsState] = useState(
    !!initialHasCredentials,
  );

  // Credential reveal state
  const [credentials, setCredentials] = useState<RevealedCredential[] | null>(
    null,
  );
  const [credentialLoading, setCredentialLoading] = useState(false);
  const [credentialError, setCredentialError] = useState<string | null>(null);
  const [visibleFields, setVisibleFields] = useState<Set<string>>(new Set());
  const [copiedCredField, setCopiedCredField] = useState<string | null>(null);

  // Card details state
  const [cardDetails, setCardDetails] = useState<any | null>(null);
  const [cardLoading, setCardLoading] = useState(false);
  const [cardError, setCardError] = useState<string | null>(null);
  const [visibleCardFields, setVisibleCardFields] = useState<Set<string>>(
    new Set(),
  );
  const [copiedCardField, setCopiedCardField] = useState<string | null>(null);

  // Credential-creation form state. The form is shown inline below the
  // toolbar when the operator clicks "Account Created", mirroring the MFA
  // quick-action pattern. On successful save, the existing
  // HUMAN_TASK_CREDENTIALS_UPDATED event flips hasCredentialsState to true
  // and triggers any open reveal panel to refresh.
  const [credentialFormOpen, setCredentialFormOpen] = useState(false);

  // MFA quick-action state. Two entry points (User MFA + Payment 3DS,
  // each a subtype-driven dropdown) feed a single inline editor. User
  // MFA is always available on active tasks since sites can demand a
  // verification code from the user in any flow (login, guest
  // checkout, phone-number confirmation, etc.). Payment 3DS is gated
  // on an attached spending mandate — without one there is no card
  // challenge to verify.
  const [loginMenuOpen, setLoginMenuOpen] = useState(false);
  const [paymentMenuOpen, setPaymentMenuOpen] = useState(false);
  const [mfaDraft, setMfaDraft] = useState<MfaCategory | null>(null);
  const [mfaDraftMessage, setMfaDraftMessage] = useState('');
  const [mfaSending, setMfaSending] = useState(false);
  const [mfaError, setMfaError] = useState<string | null>(null);
  const loginMenuRef = useRef<HTMLDivElement>(null);
  const paymentMenuRef = useRef<HTMLDivElement>(null);

  const isPhoneCall = taskType === 'phone_call';
  const isInPerson = taskType === 'in_person';
  const isBrowseSession = taskType === 'browse_session';
  const phoneDetails = isPhoneCall
    ? (taskDetails as PhoneCallTaskDetails)
    : null;
  const webDetails =
    !isPhoneCall && !isInPerson && !isBrowseSession
      ? (taskDetails as WebActionTaskDetails)
      : null;
  const browseDetails = isBrowseSession
    ? (taskDetails as BrowseSessionTaskDetails)
    : null;

  // Reset credential state when credentials are updated for this task
  const resetCredentialState = useCallback(() => {
    setCredentials(null);
    setCredentialError(null);
    setVisibleFields(new Set());
    setHasCredentialsState(true);
  }, []);

  useEffect(() => {
    if (!opsChannel) return;

    const handleCredentialsUpdated = (data: { taskId: string }) => {
      if (data.taskId === taskId) {
        resetCredentialState();
      }
    };

    opsChannel.bind(
      AgentEvents.HUMAN_TASK_CREDENTIALS_UPDATED,
      handleCredentialsUpdated,
    );

    return () => {
      opsChannel.unbind(
        AgentEvents.HUMAN_TASK_CREDENTIALS_UPDATED,
        handleCredentialsUpdated,
      );
    };
  }, [opsChannel, taskId, resetCredentialState]);

  const handleRevealCredentials = async () => {
    try {
      setCredentialLoading(true);
      setCredentialError(null);
      const token = await getAccessTokenSilently();
      const response = await api.get<RevealTaskCredentialsResponse>(
        `/human-tasks/${taskId}/credentials/reveal`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (response.data.success) {
        setCredentials(response.data.credentials ?? []);
      } else {
        setCredentialError(
          response.data.error || 'Failed to reveal credentials',
        );
      }
    } catch (err: any) {
      const msg = err.response?.data?.error || 'Failed to reveal credentials';
      setCredentialError(msg);
    } finally {
      setCredentialLoading(false);
    }
  };

  const toggleFieldVisibility = (key: string) => {
    setVisibleFields((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const handleCopyCredField = async (value: string, fieldKey: string) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopiedCredField(fieldKey);
      setTimeout(() => setCopiedCredField(null), 2000);
    } catch {
      const ta = document.createElement('textarea');
      ta.value = value;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      setCopiedCredField(fieldKey);
      setTimeout(() => setCopiedCredField(null), 2000);
    }
  };

  const maskValue = (value: string) => {
    if (value.length <= 4) return '••••';
    return '••••••••' + value.slice(-4);
  };

  const handleGetCardDetails = async (
    options: { requestAdditionalCard?: boolean } = {},
  ) => {
    try {
      setCardLoading(true);
      setCardError(null);
      const token = await getAccessTokenSilently();
      const body = options.requestAdditionalCard
        ? { requestAdditionalCard: true }
        : {};
      const response = await api.post(
        `/human-tasks/${taskId}/get-card-details`,
        body,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (response.data.success) {
        // Reset the visible-field set when we just minted a fresh VCC,
        // so the PAN/CVV start out masked again like a first reveal.
        if (options.requestAdditionalCard) {
          setVisibleCardFields(new Set());
        }
        setCardDetails(response.data);
      } else {
        setCardError(response.data.error || 'Failed to get card details');
      }
    } catch (err: any) {
      const msg = err.response?.data?.error || 'Failed to get card details';
      setCardError(msg);
    } finally {
      setCardLoading(false);
    }
  };

  const toggleCardFieldVisibility = (key: string) => {
    setVisibleCardFields((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const handleCopyCardField = async (value: string, fieldKey: string) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopiedCardField(fieldKey);
      setTimeout(() => setCopiedCardField(null), 2000);
    } catch {
      const ta = document.createElement('textarea');
      ta.value = value;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      setCopiedCardField(fieldKey);
      setTimeout(() => setCopiedCardField(null), 2000);
    }
  };

  const handleCopyTaskId = async () => {
    try {
      await navigator.clipboard.writeText(taskId);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = taskId;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handlePushToField = async () => {
    if (isPushingToField || pushSuccess) return;
    setIsPushingToField(true);
    try {
      const token = await getAccessTokenSilently();
      const response = await api.post(
        `/human-tasks/${taskId}/push-to-connecteam`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (response.data.success) {
        setPushSuccess(true);
      }
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to send to ConnectTeam');
    } finally {
      setIsPushingToField(false);
    }
  };

  // ── MFA quick-action handlers ──────────────────────────────────────
  const handleSelectUserMfa = useCallback((subtype: UserMfaSubtype) => {
    setLoginMenuOpen(false);
    setMfaError(null);
    setMfaDraft({ kind: 'user_mfa', subtype });
    setMfaDraftMessage(USER_MFA_TEMPLATES[subtype].message);
  }, []);

  const handleSelectPayment3ds = useCallback((subtype: Payment3dsSubtype) => {
    setPaymentMenuOpen(false);
    setMfaError(null);
    setMfaDraft({ kind: 'payment_3ds', subtype });
    setMfaDraftMessage(PAYMENT_3DS_TEMPLATES[subtype].message);
  }, []);

  const handleCancelMfaDraft = useCallback(() => {
    setMfaDraft(null);
    setMfaDraftMessage('');
    setMfaError(null);
  }, []);

  const handleSendMfaMessage = useCallback(async () => {
    if (!threadId || !mfaDraftMessage.trim() || !mfaDraft || mfaSending) return;
    setMfaSending(true);
    setMfaError(null);
    try {
      const token = await getAccessTokenSilently();
      // Append the hidden ops-context line for Payment 3DS so the
      // assistant knows the mandate type without the operator having
      // to spell it out (or even see it).
      const context = buildAssistantContext(
        mfaDraft,
        !!hasVcc,
        mandatePaymentMethod,
      );
      const message = context
        ? `${mfaDraftMessage.trim()}\n\n${context}`
        : mfaDraftMessage.trim();
      await api.post(
        '/agents',
        {
          thread_id: threadId,
          message,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      // On success, collapse the form. The assistant will pick up the
      // message in the chat and call `requestUserCode` itself.
      setMfaDraft(null);
      setMfaDraftMessage('');
    } catch (err: any) {
      const msg =
        err?.response?.data?.error || err?.message || 'Failed to send';
      setMfaError(msg);
    } finally {
      setMfaSending(false);
    }
  }, [
    threadId,
    mfaDraft,
    mfaDraftMessage,
    mfaSending,
    hasVcc,
    mandatePaymentMethod,
    getAccessTokenSilently,
  ]);

  // Close the User MFA / Payment 3DS dropdown menus when the user
  // clicks outside them.
  useEffect(() => {
    if (!loginMenuOpen) return;
    const handler = (e: MouseEvent) => {
      if (
        loginMenuRef.current &&
        !loginMenuRef.current.contains(e.target as Node)
      ) {
        setLoginMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [loginMenuOpen]);

  useEffect(() => {
    if (!paymentMenuOpen) return;
    const handler = (e: MouseEvent) => {
      if (
        paymentMenuRef.current &&
        !paymentMenuRef.current.contains(e.target as Node)
      ) {
        setPaymentMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [paymentMenuOpen]);

  return (
    <div className='human-task-display'>
      <div className='task-header'>
        <span className='task-type-badge'>{taskTypeLabel}</span>
        <span className='task-status'>{status}</span>
      </div>

      <h3 className='task-title'>{title}</h3>

      {/* ── Action toolbar ─────────────────────────────────────────── */}
      <div className='task-action-toolbar'>
        {hasCredentialsState && (
          <button
            className={`toolbar-btn ${credentials ? 'active' : ''}`}
            onClick={
              credentials
                ? () => {
                    setCredentials(null);
                    setVisibleFields(new Set());
                  }
                : handleRevealCredentials
            }
            disabled={readOnly || credentialLoading}
          >
            {credentialLoading
              ? 'Loading...'
              : credentials
                ? 'Hide Credentials'
                : 'Reveal Credentials'}
          </button>
        )}
        {hasMandatePayment && (
          <button
            className={`toolbar-btn ${cardDetails ? 'active' : ''}`}
            onClick={
              cardDetails
                ? () => {
                    setCardDetails(null);
                    setVisibleCardFields(new Set());
                  }
                : () => handleGetCardDetails()
            }
            disabled={readOnly || cardLoading}
          >
            {cardLoading
              ? 'Loading...'
              : cardDetails
                ? 'Hide Card'
                : 'Reveal Card Details'}
          </button>
        )}
        {/*
          Request a fresh follow-up VCC on the same mandate. Visible only
          when an agent_card is currently revealed (i.e. we know there is
          a VCC and the existing one might have been used / declined).
          Backend enforces eligibility — primary must have been used and
          mandate must still have budget — so we just surface the button
          unconditionally when a card is visible.
        */}
        {hasMandatePayment && cardDetails?.paymentMethod === 'agent_card' && (
          <button
            className='toolbar-btn'
            onClick={() =>
              handleGetCardDetails({ requestAdditionalCard: true })
            }
            disabled={readOnly || cardLoading}
            title='Request a brand-new card on this mandate. Use if the current card was already used or declined.'
          >
            {cardLoading ? 'Requesting...' : 'Request New Card'}
          </button>
        )}
        {isInPerson && (
          <button
            className={`toolbar-btn ${pushSuccess ? 'success' : ''}`}
            onClick={handlePushToField}
            disabled={readOnly || isPushingToField || pushSuccess}
          >
            {isPushingToField
              ? 'Sending...'
              : pushSuccess
                ? 'Sent to Field'
                : 'Send to Field'}
          </button>
        )}
        {/*
          User MFA — any site-side verification challenge the user can
          answer (SMS / email / authenticator / push / other). Visible
          on every active task: sites demand verification codes during
          login *and* in plenty of other flows (guest checkout, phone
          number confirmation, restaurant bookings, account recovery)
          — none of which require us to have credentials stored.
          Payment-card 3DS challenges should use the Payment 3DS
          dropdown instead.
        */}
        {threadId && !readOnly && (
          <div className='mfa-dropdown' ref={loginMenuRef}>
            <button
              className={`toolbar-btn ${
                loginMenuOpen || (mfaDraft && mfaDraft.kind === 'user_mfa')
                  ? 'active'
                  : ''
              }`}
              onClick={() => setLoginMenuOpen((v) => !v)}
              aria-haspopup='true'
              aria-expanded={loginMenuOpen}
              type='button'
            >
              User MFA ▾
            </button>
            {loginMenuOpen && (
              <div className='mfa-dropdown-menu' role='menu'>
                {(Object.keys(USER_MFA_TEMPLATES) as UserMfaSubtype[]).map(
                  (subtype) => (
                    <button
                      key={subtype}
                      type='button'
                      role='menuitem'
                      className='mfa-dropdown-item'
                      onClick={() => handleSelectUserMfa(subtype)}
                    >
                      {USER_MFA_TEMPLATES[subtype].label}
                    </button>
                  ),
                )}
              </div>
            )}
          </div>
        )}
        {/*
          Payment 3DS — card-verification challenge on the attached
          spending mandate. Subtype dropdown mirrors User MFA; the
          mandate type (VCC vs. user_card) is appended invisibly to the
          posted message so the assistant can branch without the operator
          having to think about it.
        */}
        {threadId && !readOnly && hasMandatePayment && (
          <div className='mfa-dropdown' ref={paymentMenuRef}>
            <button
              className={`toolbar-btn ${
                paymentMenuOpen || (mfaDraft && mfaDraft.kind === 'payment_3ds')
                  ? 'active'
                  : ''
              }`}
              onClick={() => setPaymentMenuOpen((v) => !v)}
              aria-haspopup='true'
              aria-expanded={paymentMenuOpen}
              type='button'
            >
              Payment 3DS ▾
            </button>
            {paymentMenuOpen && (
              <div className='mfa-dropdown-menu' role='menu'>
                {(
                  Object.keys(PAYMENT_3DS_TEMPLATES) as Payment3dsSubtype[]
                ).map((subtype) => (
                  <button
                    key={subtype}
                    type='button'
                    role='menuitem'
                    className='mfa-dropdown-item'
                    onClick={() => handleSelectPayment3ds(subtype)}
                  >
                    {PAYMENT_3DS_TEMPLATES[subtype].label}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
        {/*
          Account Created — opens the inline credential-creation form below.
          Always visible during active tasks (this button is what creates
          credentials, so we don't gate visibility on hasCredentialsState).
          The form auto-prefills site from web_action URLs and offers helper
          buttons for the user's email + a generated password, so the
          operator's decision surface is minimal.
        */}
        {!readOnly && (
          <button
            className={`toolbar-btn ${credentialFormOpen ? 'active' : ''}`}
            onClick={() => setCredentialFormOpen((v) => !v)}
            type='button'
          >
            {credentialFormOpen ? 'Cancel Account Created' : 'Account Created'}
          </button>
        )}
        <button
          className='toolbar-btn toolbar-btn--close'
          onClick={() => setShowCloseModal(true)}
          disabled={readOnly}
        >
          Close Task
        </button>
      </div>

      {/* ── Account-created inline form ────────────────────────────── */}
      {credentialFormOpen && !readOnly && (
        <HumanTaskCredentialCreationForm
          taskId={taskId}
          defaultSite={(() => {
            // Pull the host out of the web_action URL when present so the
            // operator doesn't have to retype it. Falls back to empty.
            const url = webDetails?.url;
            if (!url) return undefined;
            try {
              return new URL(url).host;
            } catch {
              return undefined;
            }
          })()}
          onCancel={() => setCredentialFormOpen(false)}
          onSuccess={() => {
            // The backend fires HUMAN_TASK_CREDENTIALS_UPDATED via Pusher;
            // our existing handler flips hasCredentialsState to true and
            // resets the reveal panel. Closing the form here is enough.
            setCredentialFormOpen(false);
          }}
        />
      )}

      {/* ── MFA quick-action inline form ───────────────────────────── */}
      {mfaDraft && (
        <div className='mfa-request-form'>
          <div className='mfa-request-form-header'>
            <span className='mfa-request-form-label'>
              {mfaDraft.kind === 'user_mfa'
                ? `User MFA · ${USER_MFA_TEMPLATES[mfaDraft.subtype].label}`
                : `Payment 3DS · ${PAYMENT_3DS_TEMPLATES[mfaDraft.subtype].label}`}
            </span>
            <span className='mfa-request-form-hint'>
              Edit the message before sending — add specifics like the brand,
              the last digits of a phone number, or anything else the assistant
              should know. Pressing Send posts this into the chat as if you
              typed it.
            </span>
          </div>
          <textarea
            className='mfa-request-form-textarea'
            value={mfaDraftMessage}
            onChange={(e) => setMfaDraftMessage(e.target.value)}
            disabled={mfaSending}
            rows={4}
            autoFocus
          />
          {mfaError && <div className='mfa-request-form-error'>{mfaError}</div>}
          <div className='mfa-request-form-actions'>
            <button
              type='button'
              className='toolbar-btn'
              onClick={handleCancelMfaDraft}
              disabled={mfaSending}
            >
              Cancel
            </button>
            <button
              type='button'
              className='toolbar-btn active'
              onClick={handleSendMfaMessage}
              disabled={mfaSending || !mfaDraftMessage.trim()}
            >
              {mfaSending ? 'Sending…' : 'Send'}
            </button>
          </div>
        </div>
      )}

      {/* ── Revealed credentials (inline) ──────────────────────────── */}
      {credentialError && (
        <div className='credentials-section'>
          <div className='credentials-error'>{credentialError}</div>
        </div>
      )}
      {credentials !== null && credentials.length > 0 && (
        <div className='credentials-section'>
          <div className='credentials-list'>
            {credentials.map((cred) => (
              <div key={cred.credentialId} className='credential-card'>
                <div className='credential-meta'>
                  <span className='credential-label'>{cred.label}</span>
                  {cred.site && (
                    <span className='credential-site'>{cred.site}</span>
                  )}
                  <span className='credential-type-badge'>
                    {cred.credentialType}
                  </span>
                </div>
                {cred.error && (
                  <div className='credential-field-error'>{cred.error}</div>
                )}
                {cred.fields && (
                  <div className='credential-fields'>
                    {Object.entries(cred.fields).map(([key, value]) => {
                      const fieldKey = `${cred.credentialId}-${key}`;
                      const isVisible = visibleFields.has(fieldKey);
                      return (
                        <div key={key} className='credential-field-row'>
                          <span className='credential-field-label'>{key}</span>
                          <div className='credential-field-value-row'>
                            <span className='credential-field-value mono'>
                              {isVisible ? value : maskValue(value)}
                            </span>
                            <button
                              className='toggle-btn'
                              onClick={() => toggleFieldVisibility(fieldKey)}
                            >
                              {isVisible ? 'Hide' : 'Show'}
                            </button>
                            <button
                              className={`copy-btn ${
                                copiedCredField === fieldKey ? 'copied' : ''
                              }`}
                              onClick={() =>
                                handleCopyCredField(value, fieldKey)
                              }
                            >
                              {copiedCredField === fieldKey
                                ? 'Copied!'
                                : 'Copy'}
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Revealed card details (inline) ─────────────────────────── */}
      {cardError && (
        <div className='card-details-section'>
          <div className='card-error'>{cardError}</div>
        </div>
      )}
      {cardDetails &&
        cardDetails.paymentMethod === 'agent_card' &&
        cardDetails.cardDetails &&
        (() => {
          // Derive usage status from the payment row the backend echoes
          // back. Company cards on the main account are single-use, so
          // any non-zero spend means this card cannot accept a further
          // merchant authorisation — surface that loudly to the operator.
          const payment = cardDetails.payment as
            | {
                quotedAmount?: string | number;
                vccSpentAmount?: string | number;
                currency?: string;
              }
            | undefined;
          const limitNum = Number(payment?.quotedAmount ?? 0);
          const spentNum = Number(payment?.vccSpentAmount ?? 0);
          const currency = payment?.currency || '';
          const hasBeenUsed = spentNum > 0;
          const remaining = Math.max(limitNum - spentNum, 0);
          const fmt = (n: number) => `${currency} ${n.toFixed(2)}`.trim();
          return (
            <div className='card-details-section'>
              <div className='card-reveal'>
                <div className='card-header-row'>
                  <div className='card-type-badge agent'>Virtual Card</div>
                  {cardDetails.isFollowup && (
                    <div className='card-type-badge agent'>Follow-up</div>
                  )}
                  {limitNum > 0 && (
                    <div
                      className={`card-limit-badge ${hasBeenUsed ? 'spent' : 'unused'}`}
                    >
                      Limit: {fmt(limitNum)}
                    </div>
                  )}
                </div>
                {hasBeenUsed ? (
                  <div className='card-usage-banner used'>
                    <strong>
                      ⚠ Already used — {fmt(spentNum)} of {fmt(limitNum)}{' '}
                      authorised.
                    </strong>{' '}
                    This card cannot accept a further merchant charge. If you
                    need to make another transaction, click{' '}
                    <em>Request New Card</em> to issue a fresh card on the
                    remaining {fmt(remaining)} budget.
                  </div>
                ) : (
                  <div className='card-usage-banner unused'>
                    Unused — full {fmt(limitNum)} available for a single
                    merchant charge.
                  </div>
                )}
                {/*
                  Summary header mirroring the user_card block: card network
                  (e.g. "Visa") + last 4 of the PAN. BE populates cardNetwork
                  via AGENT_CARD_NETWORK env or BIN detection, so it's safe
                  to show without further guessing.
                */}
                {(() => {
                  const acd = cardDetails.cardDetails as {
                    cardNumber?: string;
                    cardNetwork?: string;
                  };
                  const panDigits = (acd.cardNumber || '').replace(/\D/g, '');
                  const last4 = panDigits.slice(-4);
                  if (!acd.cardNetwork && !last4) return null;
                  return (
                    <div className='card-field'>
                      <span className='card-field-label'>Card</span>
                      <span className='card-field-value'>
                        {acd.cardNetwork
                          ? acd.cardNetwork.charAt(0).toUpperCase() +
                            acd.cardNetwork.slice(1)
                          : 'Card'}{' '}
                        {last4 ? `•••• ${last4}` : ''}
                      </span>
                    </div>
                  );
                })()}
                {Object.entries(
                  cardDetails.cardDetails as Record<string, unknown>,
                ).map(([key, value]) => {
                  if (
                    !value ||
                    key === 'cardId' ||
                    key === 'billingAddress' ||
                    // Network is rendered in the summary header above —
                    // don't surface it again in the masked per-field list.
                    key === 'cardNetwork'
                  )
                    return null;
                  const strValue = String(value);
                  const isVisible = visibleCardFields.has(key);
                  const displayKey = key
                    .replace(/([A-Z])/g, ' $1')
                    .replace(/^./, (s: string) => s.toUpperCase());
                  return (
                    <div key={key} className='card-field'>
                      <span className='card-field-label'>{displayKey}</span>
                      <div className='card-field-value-row'>
                        <span className='card-field-value mono'>
                          {isVisible ? strValue : maskValue(strValue)}
                        </span>
                        <button
                          className='toggle-btn'
                          onClick={() => toggleCardFieldVisibility(key)}
                        >
                          {isVisible ? 'Hide' : 'Show'}
                        </button>
                        <button
                          className={`copy-btn ${
                            copiedCardField === key ? 'copied' : ''
                          }`}
                          onClick={() => handleCopyCardField(strValue, key)}
                        >
                          {copiedCardField === key ? 'Copied!' : 'Copy'}
                        </button>
                      </div>
                    </div>
                  );
                })}
                {cardDetails.cardDetails.billingAddress &&
                  (() => {
                    const b = cardDetails.cardDetails.billingAddress as Record<
                      string,
                      string | undefined
                    >;
                    const rows: {
                      label: string;
                      key: string;
                      value?: string;
                    }[] = [
                      {
                        label: 'Billing Address',
                        key: 'billingAddressLine1',
                        value: b.line1,
                      },
                      {
                        label: 'Address Line 2',
                        key: 'billingAddressLine2',
                        value: b.line2,
                      },
                      { label: 'City', key: 'billingCity', value: b.city },
                      { label: 'State', key: 'billingState', value: b.state },
                      {
                        label: 'Postcode',
                        key: 'billingPostcode',
                        value: b.postcode,
                      },
                      {
                        label: 'Country',
                        key: 'billingCountry',
                        value: b.country,
                      },
                    ];
                    return rows
                      .filter((r) => r.value)
                      .map((r) => (
                        <div key={r.key} className='card-field'>
                          <span className='card-field-label'>{r.label}</span>
                          <div className='card-field-value-row'>
                            <span className='card-field-value'>{r.value}</span>
                            <button
                              className={`copy-btn ${
                                copiedCardField === r.key ? 'copied' : ''
                              }`}
                              onClick={() =>
                                handleCopyCardField(String(r.value), r.key)
                              }
                            >
                              {copiedCardField === r.key ? 'Copied!' : 'Copy'}
                            </button>
                          </div>
                        </div>
                      ));
                  })()}
              </div>
            </div>
          );
        })()}
      {cardDetails &&
        cardDetails.paymentMethod === 'user_card' &&
        cardDetails.cardDetails && (
          <div className='card-details-section'>
            <div className='card-reveal'>
              <div className='card-type-badge user'>User Card</div>
              {cardDetails.cardDetails.cardLastFour && (
                <div className='card-field'>
                  <span className='card-field-label'>Card</span>
                  <span className='card-field-value'>
                    {cardDetails.cardDetails.cardNetwork
                      ? cardDetails.cardDetails.cardNetwork
                          .charAt(0)
                          .toUpperCase() +
                        cardDetails.cardDetails.cardNetwork.slice(1)
                      : 'Card'}{' '}
                    •••• {cardDetails.cardDetails.cardLastFour}
                  </span>
                </div>
              )}
              {cardDetails.cardDetails.fields &&
                Object.entries(
                  cardDetails.cardDetails.fields as Record<string, string>,
                ).map(([key, value]) => {
                  if (!value) return null;
                  const isVisible = visibleCardFields.has(key);
                  return (
                    <div key={key} className='card-field'>
                      <span className='card-field-label'>
                        {(
                          {
                            cardholderName: 'Cardholder',
                            cardNumber: 'Card Number',
                            expiryDate: 'Expiry',
                            cvv: 'CVV',
                            billingAddressLine1: 'Billing Address',
                            billingAddressLine2: 'Address Line 2',
                            billingCity: 'City',
                            billingPostcode: 'Postcode',
                            billingCountry: 'Country',
                          } as Record<string, string>
                        )[key] || key}
                      </span>
                      <div className='card-field-value-row'>
                        <span className='card-field-value mono'>
                          {isVisible ? value : maskValue(String(value))}
                        </span>
                        <button
                          className='toggle-btn'
                          onClick={() => toggleCardFieldVisibility(key)}
                        >
                          {isVisible ? 'Hide' : 'Show'}
                        </button>
                        <button
                          className={`copy-btn ${
                            copiedCardField === key ? 'copied' : ''
                          }`}
                          onClick={() =>
                            handleCopyCardField(String(value), key)
                          }
                        >
                          {copiedCardField === key ? 'Copied!' : 'Copy'}
                        </button>
                      </div>
                    </div>
                  );
                })}
            </div>
          </div>
        )}

      {cardDetails && cardDetails.paymentMethod === 'merchant_stored' && (
        <div className='card-details-section'>
          <div className='card-reveal'>
            <div className='card-type-badge user'>Merchant-Stored Card</div>
            {cardDetails.cardHint && (
              <div className='card-field'>
                <span className='card-field-label'>Card hint</span>
                <span className='card-field-value'>{cardDetails.cardHint}</span>
              </div>
            )}
            <div className='card-field'>
              <span className='card-field-label'>Info</span>
              <span className='card-field-value'>
                Card is saved at the merchant — no details to reveal. If a CVV
                is needed during checkout, use the Payment 3DS → CVV needed
                button above.
              </span>
            </div>
          </div>
        </div>
      )}

      {/* ── Task info ──────────────────────────────────────────────── */}
      {deadline && (
        <div className='task-deadline'>
          <span className='deadline-label'>Deadline:</span>
          <span className='deadline-value'>
            {new Date(deadline).toLocaleString('en-GB', {
              dateStyle: 'medium',
              timeStyle: 'short',
            })}
          </span>
        </div>
      )}
      <p className='task-description'>{description}</p>

      <div className='task-id-row'>
        <span className='task-id-label'>Task ID (for Chrome Extension):</span>
        <div className='task-id-value-wrapper'>
          <code className='task-id-value'>{taskId}</code>
          <button
            className={`copy-btn ${copied ? 'copied' : ''}`}
            onClick={handleCopyTaskId}
            title='Copy Task ID'
          >
            {copied ? 'Copied!' : 'Copy'}
          </button>
        </div>
      </div>

      <div className='task-details'>
        {isPhoneCall && phoneDetails && (
          <>
            <div className='detail-row'>
              <span className='detail-label'>Phone Number:</span>
              <span className='detail-value'>{phoneDetails.phoneNumber}</span>
            </div>
            {phoneDetails.contactName && (
              <div className='detail-row'>
                <span className='detail-label'>Contact Name:</span>
                <span className='detail-value'>{phoneDetails.contactName}</span>
              </div>
            )}
            <div className='detail-row'>
              <span className='detail-label'>Purpose:</span>
              <span className='detail-value'>{phoneDetails.purpose}</span>
            </div>
            {phoneDetails.scriptGuidance && (
              <div className='detail-row'>
                <span className='detail-label'>Script Guidance:</span>
                <span className='detail-value script-guidance'>
                  {phoneDetails.scriptGuidance}
                </span>
              </div>
            )}
          </>
        )}

        {!isPhoneCall && webDetails && (
          <>
            <div className='detail-row'>
              <span className='detail-label'>URL:</span>
              <a
                href={webDetails.url}
                target='_blank'
                rel='noopener noreferrer'
                className='detail-value detail-link'
              >
                {webDetails.url}
              </a>
            </div>
            {webDetails.actionType && (
              <div className='detail-row'>
                <span className='detail-label'>Action Type:</span>
                <span className='detail-value'>
                  {webDetails.actionType.replace(/_/g, ' ')}
                </span>
              </div>
            )}
            <div className='detail-row'>
              <span className='detail-label'>Instructions:</span>
              <span className='detail-value script-guidance'>
                {webDetails.instructions}
              </span>
            </div>
            {webDetails.formFields &&
              Object.keys(webDetails.formFields).length > 0 && (
                <div className='detail-row'>
                  <span className='detail-label'>Form Fields:</span>
                  <div className='detail-value form-fields'>
                    {Object.entries(webDetails.formFields).map(
                      ([key, value]) => (
                        <div key={key} className='form-field'>
                          <strong>{key}:</strong> {value}
                        </div>
                      ),
                    )}
                  </div>
                </div>
              )}
            {webDetails.expectedOutcome && (
              <div className='detail-row'>
                <span className='detail-label'>Expected Outcome:</span>
                <span className='detail-value'>
                  {webDetails.expectedOutcome}
                </span>
              </div>
            )}
          </>
        )}

        {isInPerson && (
          <>
            {(taskDetails as any)?.instructions && (
              <div className='detail-row'>
                <span className='detail-label'>Instructions:</span>
                <span className='detail-value script-guidance'>
                  {(taskDetails as any).instructions}
                </span>
              </div>
            )}
            {(taskDetails as any)?.url && (
              <div className='detail-row'>
                <span className='detail-label'>Reference Link:</span>
                <a
                  href={(taskDetails as any).url}
                  target='_blank'
                  rel='noopener noreferrer'
                  className='detail-value detail-link'
                >
                  {(taskDetails as any).url}
                </a>
              </div>
            )}
            {(taskDetails as any)?.expectedOutcome && (
              <div className='detail-row'>
                <span className='detail-label'>Expected Outcome:</span>
                <span className='detail-value'>
                  {(taskDetails as any).expectedOutcome}
                </span>
              </div>
            )}
          </>
        )}

        {isBrowseSession && browseDetails && (
          <>
            <div className='detail-row'>
              <span className='detail-label'>Platform:</span>
              <span className='detail-value'>{browseDetails.platform}</span>
            </div>
            <div className='detail-row'>
              <span className='detail-label'>Starting URL:</span>
              <a
                href={browseDetails.url}
                target='_blank'
                rel='noopener noreferrer'
                className='detail-value detail-link'
              >
                {browseDetails.url}
              </a>
            </div>
            {browseDetails.numberOfOptions && (
              <div className='detail-row'>
                <span className='detail-label'>Options Needed:</span>
                <span className='detail-value'>
                  {browseDetails.numberOfOptions}
                </span>
              </div>
            )}
            {browseDetails.browsingBrief && (
              <div className='detail-row'>
                <span className='detail-label'>Brief:</span>
                <span className='detail-value script-guidance'>
                  {browseDetails.browsingBrief}
                </span>
              </div>
            )}
          </>
        )}
      </div>

      {/* ── ConnectTeam status ─────────────────────────────────────── */}
      {connecteamStatus && (
        <div className='connecteam-section'>
          <div className='connecteam-header'>ConnectTeam</div>
          <div className='connecteam-details'>
            <div className='connecteam-row'>
              <span className='connecteam-label'>Status:</span>
              <span
                className={`connecteam-status-pill ${connecteamStatusColor(connecteamStatus)}`}
              >
                {connecteamStatusLabel(connecteamStatus)}
              </span>
            </div>
            {connecteamWorkerName && (
              <div className='connecteam-row'>
                <span className='connecteam-label'>Worker:</span>
                <span className='connecteam-value'>{connecteamWorkerName}</span>
              </div>
            )}
          </div>
        </div>
      )}

      <CloseTaskModal
        show={showCloseModal}
        taskId={taskId}
        acceptedAt={acceptedAt}
        onClose={() => setShowCloseModal(false)}
        onSuccess={() => {
          setShowCloseModal(false);
          onTaskClosed?.();
          navigate('/tasks');
        }}
      />
    </div>
  );
};

export default HumanTaskDisplay;
